import useTranslator from "@hooks/use-translator";
import { Input } from "@components/common/input";
import { Button } from "@components/common/button";
import { PlusCircle } from "lucide-react";
import axios from "axios";
import { SERVER_URL, STATUS_DICTIONARY_ENDPOINT } from "@pma/utils/api-routes";
import { useMutation } from "@tanstack/react-query";
import { useAuth } from "@stores/auth-store";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useRef, useState } from "react";
import { DictType } from "@pma/types";
import { toast } from "@hooks/use-toast";
import { HexColorPicker } from "react-colorful";
import { cn } from "@pma/utils/helpers";

interface Props {
  option: "task" | "project" | "user";
  refetch?: CallableFunction;
}
const AddStatusForm = ({ option, refetch }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useState<DictType>({
    status_name: "",
    color: "#6465ca",
  });
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const isHebrew = locale === "he";

  const colorPickerRef = useRef<HTMLDivElement | null>(null);

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const orgId = authConfig?.last_organization_id;
  const userId = authConfig?.id;

  const forProject = option === "project";
  const forTask = option === "task";

  const addStatus = async () => {
    const response = await axios.post(
      SERVER_URL + STATUS_DICTIONARY_ENDPOINT + "/add",
      {
        ...status,
        name: status?.status_name,
        canceled: false,
        finished: false,
        for_task: forTask,
        for_project: forProject,
        organization_id: orgId,
        user_id: userId,
        is_active: true,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addStatusMutation = useMutation({
    mutationKey: ["add-status"],
    mutationFn: addStatus,
    onSuccess: (data) => {
      setStatus({
        status_name: "",
        color: "#6465ca",
      });
      refetch && refetch();
    },
    onError: (error: DictType) => {
      toast({
        title: translate("Error"),
        description: error?.response?.data.message ||
          translate("Error occurred while updating") +
            " " +
            translate("status"),
        variant: "error",
      });
    },
  });

  const onSubmit = (e: any) => {
    e.preventDefault();
    addStatusMutation.mutate();
  };

  return (
    <form
      onSubmit={onSubmit}
      className={cn("flex items-center gap-2", isHebrew ? "rtl" : "ltr")}
      onMouseLeave={() => setIsOpen(false)}
    >
      <div className="relative">
        <Button
          className="w-full"
          style={{
            background: status?.color || "blue",
          }}
          onClick={() => setIsOpen(true)}
        />
        {isOpen && (
          <div className="absolute" ref={colorPickerRef}>
            <HexColorPicker
              className=""
              defaultValue={status?.color}
              onChange={(e: any) =>
                setStatus((prev) => ({ ...prev, color: e }))
              }
            />
          </div>
        )}
      </div>
      <Input
        placeholder={translate("Status")}
        className="rounded-full"
        required
        value={status?.status_name}
        onChange={(e) =>
          setStatus((prev) => ({ ...prev, status_name: e.target.value }))
        }
      />
      <Button size={"sm"} className="rounded-full h-7 w-7 m-0 p-0">
        <PlusCircle className=" h-7 w-7 rounded-full" />
      </Button>
    </form>
  );
};

export default AddStatusForm;
